import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getPackagingPurchaseOrderById } from 'src/redux/slices/packaging-purchase-order';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';
import PackagingPurchaseOrderDetails from '../packaging-purchase-order-details';

type Props = {
  id: string;
};

export default function PackagingPurchaseOrderDetailView({ id }: Props) {
  const { order, status, error } = useAppSelector((state) => state.packagingPurchaseOrder);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getPackagingPurchaseOrderById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load purchase order details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.packagingPurchaseOrder.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Purchase Orders
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  if (status === 'success' && order) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Purchase Order Details"
          links={[
            {
              name: 'Purchase Orders',
              href: paths.dashboard.packagingPurchaseOrder.root,
            },
            { name: 'Purchase Order Details' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <PackagingPurchaseOrderDetails currentPurchaseOrder={order} />
      </Container>
    );
  }

  return <span />;
}
